"use client";

import React, { useMemo, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import {
  AppBar,
  Avatar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import { useAuth } from "../hooks/useAuth";

type HeaderProps = {
  onMenuClick: () => void;
  drawerWidth: number;
};

export default function Header({ onMenuClick, drawerWidth }: HeaderProps) {
  const pathname = usePathname() || "/";
  const router = useRouter();
  const { user, logout } = useAuth();
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);

  const openProfileMenu = (event: React.MouseEvent<HTMLElement>) =>
    setProfileAnchor(event.currentTarget);
  const closeProfileMenu = () => setProfileAnchor(null);

  const sectionLabel = useMemo(() => {
    if (pathname.startsWith("/dashboard/api-keys"))
      return "API Key Activity";
    if (pathname.startsWith("/dashboard")) return "IAM Dashboard";
    if (pathname.startsWith("/users/api-keys")) return "API Key Management";
    if (pathname.startsWith("/users")) return "User Management";
    if (pathname.startsWith("/hierarchy")) return "Entity Hierarchy";
    if (pathname.startsWith("/data-sync/enterprise"))
      return "Enterprise Source Data";
    if (pathname.startsWith("/data-sync/tenants")) return "Tenant Setup";
    if (pathname.startsWith("/data-sync/scheduler"))
      return "Tenant Data Sync Scheduler";
    if (pathname.startsWith("/data-sync")) return "Tenant Data Sync";
    if (pathname.startsWith("/roles")) return "Role Management";
    if (pathname.startsWith("/permissions")) return "Permission Catalog";
    if (pathname.startsWith("/access")) return "Access Control";
    return "IAM Platform";
  }, [pathname]);

  return (
    <AppBar
      position="fixed"
      color="transparent"
      sx={{
        width: { lg: `calc(100% - ${drawerWidth}px)` },
        ml: { lg: `${drawerWidth}px` },
        backdropFilter: "blur(14px)",
        backgroundColor: "rgba(255,255,255,0.92)",
        borderBottom: "1px solid rgba(21,34,56,0.1)",
        borderRadius: 0,
        height: "117px",
      }}
      elevation={0}
    >
      <Toolbar sx={{ py: 1.5, gap: 2, height: "100%" }}>
        <IconButton
          color="primary"
          onClick={onMenuClick}
          edge="start"
          sx={{ display: { lg: "none" } }}
          aria-label="Open navigation"
        >
          <MenuIcon />
        </IconButton>

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: "0.08em" }}
          >
            Origin-North IAM
          </Typography>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {sectionLabel}
            </Typography>
            {pathname.startsWith("/users") && (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", opacity: 0.85 }}
              >
                Manage users, assign roles, and control access for your
                organization.
              </Typography>
            )}
          </Box>
        </Box>

        <Tooltip title="Dashboard" arrow>
          <IconButton
            color="primary"
            component={Link}
            href="/dashboard"
            aria-label="Go to dashboard"
          >
            <DashboardOutlinedIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title={user?.email || "Account"} arrow>
          <IconButton
            color="primary"
            onClick={openProfileMenu}
            size="large"
            aria-label="Account options"
            sx={{ border: "1px solid rgba(76,154,255,0.3)" }}
          >
            <Avatar sx={{ bgcolor: "secondary.main", width: 36, height: 36 }}>
              <AccountCircleIcon />
            </Avatar>
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={profileAnchor}
          open={Boolean(profileAnchor)}
          onClose={closeProfileMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem
            onClick={() => {
              closeProfileMenu();
              router.push("/profile");
            }}
          >
            <AccountCircleIcon fontSize="small" sx={{ mr: 1 }} /> Profile &
            Preferences
          </MenuItem>
          <MenuItem
            onClick={async () => {
              closeProfileMenu();
              try {
                await logout();
              } finally {
                // redirect to login after session cleared
                void router.replace("/login");
              }
            }}
          >
            <LogoutIcon fontSize="small" sx={{ mr: 1 }} /> Sign out
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
